import React, { useEffect, useState, useMemo } from 'react';
import { Input } from '../components/ui/input-simple';
import { Button } from '../components/ui/button-simple';
import { servicesAPI } from '../services/api';
import { adminOffers as fallbackOffers } from '../data/mockData';
import { Search, Briefcase, Phone, Star, AlertCircle, CheckCircle, Eye, X } from 'lucide-react';

// Animations
const styles = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in-up { animation: fadeInUp 0.5s ease-out forwards; }
  .animate-fade-in-up:nth-child(1) { animation-delay: 0.1s; }
  .animate-fade-in-up:nth-child(2) { animation-delay: 0.15s; }
  .animate-fade-in-up:nth-child(3) { animation-delay: 0.2s; }
  .animate-fade-in-up:nth-child(4) { animation-delay: 0.25s; }
  .animate-fade-in-up:nth-child(5) { animation-delay: 0.3s; }
  .animate-fade-in-up:nth-child(6) { animation-delay: 0.35s; }
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-slide-down { animation: slideDown 0.6s ease-out forwards; }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  if (!document.head.querySelector('style[data-user-services-animations]')) {
    styleSheet.setAttribute('data-user-services-animations', 'true');
    document.head.appendChild(styleSheet);
  }
}

export default function Services() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOffer, setSelectedOffer] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        if (servicesAPI && servicesAPI.getAll) {
          const res = await servicesAPI.getAll();
          const data = Array.isArray(res) ? res : (res?.data || res?.rows || []);
          setOffers(data);
        } else {
          setOffers(fallbackOffers || []);
        }
      } catch (err) {
        setOffers(fallbackOffers || []);
        setError('Failed to load services from server. Showing cached data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Normalize each offer
  const normalizedOffers = useMemo(() => {
    return offers.map((o) => ({
      id: o.service_id || o.id || o.title,
      title: o.title || o.service_name || 'Untitled Service',
      description: o.description || o.service_description || '',
      contact: o.contact || o.service_contact || '—',
      active: (o.is_active === 1 || o.is_active === true || o.active === true),
      featured: o.is_featured === 1 || o.is_featured === true || false,
    }));
  }, [offers]);

  // Filter logic
  const filteredOffers = useMemo(() => {
    return normalizedOffers.filter(o => {
      const matchesSearch = o.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            o.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            o.contact.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' ||
                            (statusFilter === 'active' && o.active) ||
                            (statusFilter === 'inactive' && !o.active);
      return matchesSearch && matchesStatus;
    });
  }, [normalizedOffers, searchTerm, statusFilter]);

  // Stats
  const totalOffers = normalizedOffers.length;
  const activeOffers = normalizedOffers.filter(o => o.active).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-8 bg-gradient-to-r from-amber-600 to-amber-700 rounded-2xl shadow-xl p-5 text-white animate-slide-down relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                <Briefcase className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-1">Services & Offers</h1>
                <p className="text-amber-50 max-w-2xl text-sm">
                  Browse available services offered by BISU — Bilar Campus for cacao industry stakeholders
                </p>
              </div>
            </div>
            {/* Quick stats in header */}
            <div className="hidden md:flex items-center gap-4">
              <div className="text-center bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2">
                <p className="text-2xl font-bold">{totalOffers}</p>
                <p className="text-xs text-amber-100">Total</p>
              </div>
              <div className="text-center bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2">
                <p className="text-2xl font-bold">{activeOffers}</p>
                <p className="text-xs text-amber-100">Active</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search services by name, description, or contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 border-gray-300 focus:border-amber-500 focus:ring-amber-500 rounded-xl"
                aria-label="Search services"
              />
            </div>
            {/* Status Filter */}
            <div className="relative">
              <CheckCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 z-10" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-10 w-full h-12 border border-gray-300 rounded-xl focus:border-amber-500 focus:ring-amber-500 bg-white appearance-none cursor-pointer text-sm"
                aria-label="Filter by status"
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
          </div>
          {/* Results count */}
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Showing <span className="font-semibold text-amber-600">{filteredOffers.length}</span> of {totalOffers} services
            </span>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="flex items-center gap-1 text-amber-700 hover:text-amber-900 transition-colors text-sm"
              >
                <X className="h-4 w-4" />
                Clear search
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">Loading services...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6 animate-fade-in">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-800 font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredOffers.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg border border-gray-200 animate-fade-in">
            <Briefcase className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No services found</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your search or filter to find what you\'re looking for.'
                : 'No services are available at the moment. Please check back later.'}
            </p>
            {(searchTerm || statusFilter !== 'all') && (
              <button
                onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 transition-colors text-sm"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}

        {/* Services Grid */}
        {!loading && filteredOffers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredOffers.map((offer) => (
              <div
                key={offer.id}
                className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-amber-300 overflow-hidden flex flex-col animate-fade-in-up"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-amber-50 to-amber-100 p-4 border-b border-amber-200 flex-shrink-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="bg-amber-200/60 p-2 rounded-lg flex-shrink-0 mt-0.5">
                        <Briefcase className="h-5 w-5 text-amber-700" />
                      </div>
                      <h3 className="text-lg text-amber-900 font-bold group-hover:text-amber-700 transition-colors line-clamp-2 leading-snug">
                        {offer.title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {offer.featured && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold border border-yellow-200">
                          <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                          Featured
                        </span>
                      )}
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        offer.active
                          ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                          : 'bg-gray-100 text-gray-500 border border-gray-200'
                      }`}>
                        {offer.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-4 flex-1 flex flex-col">
                  <p className="text-sm text-gray-600 leading-relaxed line-clamp-4 flex-1">
                    {offer.description || 'No description available for this service.'}
                  </p>

                  {/* Contact Row */}
                  <div className="mt-4 flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                    <Phone className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm text-gray-600 font-medium">Contact:</span>
                    <span className="text-sm text-gray-900 font-semibold truncate">{offer.contact}</span>
                  </div>

                  {/* Action */}
                  <div className="mt-4 flex-shrink-0">
                    <Button
                      className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-semibold flex items-center justify-center gap-2 rounded-xl shadow-md hover:shadow-lg transition-all py-2.5 h-10"
                      onClick={() => setSelectedOffer(offer)}
                    >
                      <Eye className="h-4 w-4" />
                      View Details
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Detail Modal */}
        {selectedOffer && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setSelectedOffer(null)}
            />
            {/* Modal Card */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-200">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-5 text-white">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1 min-w-0 pr-4">
                    <div className="bg-white/20 p-2.5 rounded-xl flex-shrink-0">
                      <Briefcase className="h-6 w-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl font-bold leading-snug">{selectedOffer.title}</h2>
                      <div className="flex items-center gap-2 mt-2">
                        {selectedOffer.featured && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-400/30 text-yellow-100 rounded-full text-xs font-semibold">
                            <Star className="h-3 w-3 fill-yellow-300 text-yellow-300" />
                            Featured
                          </span>
                        )}
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          selectedOffer.active
                            ? 'bg-emerald-400/30 text-emerald-100'
                            : 'bg-white/20 text-white/70'
                        }`}>
                          {selectedOffer.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedOffer(null)}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-xl transition-colors flex-shrink-0"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              {/* Modal Body */}
              <div className="p-6 space-y-5">
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Description</h4>
                  <p className="text-gray-700 leading-relaxed">
                    {selectedOffer.description || 'No description available.'}
                  </p>
                </div>
                <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <Phone className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Contact Information</p>
                    <p className="text-gray-900 font-semibold mt-0.5">{selectedOffer.contact}</p>
                  </div>
                </div>
              </div>
              {/* Modal Footer */}
              <div className="border-t border-gray-100 p-4 flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => setSelectedOffer(null)}
                  className="px-6 rounded-xl hover:bg-amber-50 hover:border-amber-300"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
